import { useState } from 'react'
import { Mail, Phone, Star, UserRound } from 'lucide-react'
import { formatCurrency, formatDateLong } from '@hotel/shared/utils'
import { useCustomerLogin, useCustomerRegister, useCustomerReservations } from '../api/hooks'
import type { Customer } from '../api/types'
import { TaxNote } from '../components/PriceBreakdown'

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  checked_in: 'Check-in',
  checked_out: 'Check-out',
  cancelled: 'Cancelada',
  no_show: 'No Show',
}

export function AccountPage({
  customer,
  setCustomerAuth,
}: {
  customer: Customer | null
  setCustomerAuth: (next: { token: string; customer: Customer } | null) => void
}) {
  const [form, setForm] = useState({
    email: customer?.email || '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
  })

  const reservationsQuery = useCustomerReservations(customer?.id)
  const loginMutation = useCustomerLogin()
  const registerMutation = useCustomerRegister()

  const handleLogin = () => {
    loginMutation.mutate(
      { email: form.email, password: form.password },
      { onSuccess: (data) => setCustomerAuth(data) },
    )
  }

  const handleRegister = () => {
    registerMutation.mutate(form, {
      onSuccess: (data) => setCustomerAuth(data),
    })
  }

  return (
    <main className="content-section booking-layout">
      <section className="booking-main">
        <div className="section-intro left">
          <span className="eyebrow">Área de cliente</span>
          <h1>{customer ? `Hola, ${customer.first_name}` : 'Ingresa para ver tus reservas'}</h1>
          <p>Si no tienes cuenta, puedes crearla con correo y teléfono obligatorios.</p>
        </div>
        {customer ? (
          <div className="reservation-list">
            {(reservationsQuery.data || []).map((reservation) => (
              <article key={reservation.id} className="reservation-card">
                <div>
                  <span className="eyebrow">Confirmación</span>
                  <h3>{reservation.confirmation_code}</h3>
                  <p>{reservation.room_type_name}</p>
                  <span className={`reservation-status status-${reservation.status}`}>
                    {statusLabels[reservation.status] || reservation.status}
                  </span>
                </div>
                <div className="reservation-meta">
                  <span>{formatDateLong(reservation.check_in_date)} a {formatDateLong(reservation.check_out_date)}</span>
                  <strong>Total de estadía {formatCurrency(reservation.pricing.total)}</strong>
                  <TaxNote pricing={reservation.pricing} />
                </div>
              </article>
            ))}
            {reservationsQuery.data?.length === 0 && <div className="notice">Tu cuenta todavía no tiene reservaciones asociadas.</div>}
          </div>
        ) : (
          <div className="dual-auth">
            <div className="form-card">
              <h3>Ingresar</h3>
              <div className="form-grid compact">
                <div className="field full"><label>Correo</label><input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
                <div className="field full"><label>Contraseña</label><input type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} /></div>
                <button className="primary-button full-width" onClick={handleLogin}>Ingresar</button>
              </div>
            </div>
            <div className="form-card">
              <h3>Crear cuenta</h3>
              <div className="form-grid compact">
                <div className="field"><label>Nombre</label><input value={form.first_name} onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))} /></div>
                <div className="field"><label>Apellido</label><input value={form.last_name} onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))} /></div>
                <div className="field"><label>Correo</label><input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
                <div className="field"><label>Teléfono</label><input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} /></div>
                <div className="field full"><label>Contraseña</label><input type="password" value={form.password} onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))} /></div>
                <button className="secondary-button full-width" onClick={handleRegister}>Crear cuenta</button>
              </div>
            </div>
          </div>
        )}
      </section>

      <aside className="booking-sidebar">
        <div className="summary-card alt">
          <div className="info-list">
            <div><UserRound size={18} /> Cuenta opcional para seguimiento de reservas</div>
            <div><Phone size={18} /> Teléfono obligatorio</div>
            <div><Mail size={18} /> Correo obligatorio</div>
            <div><Star size={18} /> Reserva pública sin exponer ocupación interna</div>
          </div>
        </div>
      </aside>
    </main>
  )
}
